import {
    cloneElement,
    isValidElement,
    type ReactElement,
    type ReactNode
} from 'react';
import Box from '@mui/material/Box';

/**
 * Merge a warning icon into an Autocomplete's endAdornment. The Autocomplete
 * centers its indicators in an absolutely-positioned container; a sibling
 * adornment would sit below centre (the filled variant keeps its label
 * clearance as root padding — 19px at the top and none at the bottom, which
 * puts anything the input root centers 9.5px low), so the icon must ride
 * INSIDE that container. Pass the icon `bare` — the container positions it.
 *
 * And when there is NO container: MUI renders one only for the indicators it
 * actually has, and our fields are `freeSolo` (no popup indicator) and carry
 * the warning only while they are EMPTY (no clear indicator) — so the usual
 * case is no container at all. Then we build the same one, rather than letting
 * the icon drop into the padded flex row and sit low.
 */
export function mergeAutocompleteAdornment(
    endAdornment: ReactNode,
    warning: ReactNode
): ReactNode {
    return intoIndicatorBox(endAdornment, warning, 'first');
}

/**
 * Add an indicator BUTTON after MUI's own — last in the row, where a popup
 * arrow would sit, so the clear X keeps its place beside it. The button should
 * carry FIELD_END_BUTTON_SX to match them, and the Autocomplete
 * `indicatorRoomSx` to keep typed text clear of it (see ./fieldEndButton).
 */
export function appendAutocompleteIndicator(
    endAdornment: ReactNode,
    indicator: ReactNode
): ReactNode {
    return intoIndicatorBox(endAdornment, indicator, 'last');
}

function intoIndicatorBox(
    endAdornment: ReactNode,
    added: ReactNode,
    place: 'first' | 'last'
): ReactNode {
    if (!added) {
        return endAdornment;
    }
    if (isValidElement(endAdornment)) {
        const container = endAdornment as ReactElement<{
            children?: ReactNode;
        }>;
        return place === 'first'
            ? cloneElement(container, {}, added, container.props.children)
            : cloneElement(container, {}, container.props.children, added);
    }
    return (
        <Box
            /**
             * MUI's own AutocompleteEndAdornment, written out: centred on the
             * input root's BORDER box, which is what makes it immune to that
             * asymmetric padding. `right: 9` is where the filled variant puts
             * the indicators, so a warning lines up with the clear icon that
             * replaces it the moment the field is filled.
             */
            sx={{
                position: 'absolute',
                right: 9,
                top: '50%',
                transform: 'translateY(-50%)'
            }}
        >
            {place === 'first' ? added : endAdornment}
            {place === 'first' ? endAdornment : added}
        </Box>
    );
}
